import { type DateRange } from 'react-day-picker'

export const DATE_RANGE_LABELS = {
  CUSTOM: 'Custom',
  PAST_7_DAYS: 'Past 7 days',
  OLDER_THAN_7_DAYS: 'Older than 7 days'
} as const

export const RETIRED_TODAY_LABEL = 'Today'

export const EARLIEST_START_DATE = Object.freeze(new Date(2022, 0, 1))

export type ActivationDate = Date | string | null | undefined

export interface PredefinedRange {
  label: string
  range?: DateRange
}

export const startOfToday = (): Date => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return today
}

export const addDays = (date: Date, days: number): Date => {
  const next = new Date(date)
  next.setDate(date.getDate() + days)
  return next
}

export const startOfPreviousMonth = (date: Date = new Date()): Date =>
  new Date(date.getFullYear(), date.getMonth() - 1, 1)

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/

export const toLocalDate = (utcStr: string): Date => {
  const [year, month, day] = utcStr.substring(0, 10).split('-').map(Number)
  return new Date(year, month - 1, day)
}

const parseActivationDate = (activatedAt: Date | string): Date | null => {
  if (activatedAt instanceof Date) {
    if (Number.isNaN(activatedAt.getTime())) return null
    const copy = new Date(activatedAt)
    copy.setHours(0, 0, 0, 0)
    return copy
  }

  if (ISO_DATE.test(activatedAt)) return toLocalDate(activatedAt)

  const parsed = new Date(activatedAt)
  if (Number.isNaN(parsed.getTime())) return null
  parsed.setHours(0, 0, 0, 0)
  return parsed
}

export const resolveOlderThanStartDate = (
  activatedAt: ActivationDate
): Date => {
  if (!activatedAt) return new Date(EARLIEST_START_DATE)

  const activated = parseActivationDate(activatedAt)
  if (!activated) return new Date(EARLIEST_START_DATE)

  return activated > EARLIEST_START_DATE
    ? activated
    : new Date(EARLIEST_START_DATE)
}

export const buildOlderThan7DaysRange = (
  activatedAt?: ActivationDate
): DateRange => ({
  from: resolveOlderThanStartDate(activatedAt),
  to: addDays(startOfToday(), -7)
})

export const buildPast7DaysRange = (): DateRange => {
  const today = startOfToday()
  return { from: addDays(today, -6), to: today }
}

export const buildPredefinedRanges = (
  activatedAt?: ActivationDate
): PredefinedRange[] => [
  { label: DATE_RANGE_LABELS.CUSTOM },
  { label: DATE_RANGE_LABELS.PAST_7_DAYS, range: buildPast7DaysRange() },
  {
    label: DATE_RANGE_LABELS.OLDER_THAN_7_DAYS,
    range: buildOlderThan7DaysRange(activatedAt)
  }
]
